//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';

import { MIN_PANEL_SIZE, type PanelSize } from './panelGeometry';
import {
  FALLBACK_PANEL_ICON, isKnownPanelIcon, resolvePanelIcon
} from './panelIcons';
import { type PluginSurfaceRow } from '@/components/Header/pluginSurfaces';
import { type PanelSpec } from '@/contexts/PanelContext';
import { type PluginPanelConfig } from '@/services/apiTypes';

/**
 * A plugin panel's registry id.
 *
 * Namespaced on both halves because neither alone is unique: one plugin may
 * ship several panel surfaces, and two plugins may name a surface the same
 * thing. The `plugin:` prefix keeps the whole space clear of the built-in
 * clients, which register under bare names like `chat`.
 */
export const pluginPanelId = (plugin: string, key: string): string =>
  `plugin:${plugin}:${key}`;

/**
 * The panel a surface should open instead of its modal, or null when it has
 * none.
 *
 * The declared form is the whole decision. A surface carrying panel config
 * without declaring the form is rejected at validation, so this never has to
 * choose between two ways of asking for the same thing.
 */
export const panelIdForRow = (row: PluginSurfaceRow): string | null =>
  row.surface.form === 'panel' ?
    pluginPanelId(row.plugin.name, row.key) : null;

// A one-sided minimum leaves the other axis to the shared floor. Spelling
// that axis out rather than leaving it undefined keeps `minSize` a whole
// `PanelSize`, which is what the geometry module and the resize handle read.
const toMinSize = (panel: PluginPanelConfig): PanelSize | undefined => {
  if (panel.min_width === undefined && panel.min_height === undefined)
    return undefined;
  return {
    w: panel.min_width ?? MIN_PANEL_SIZE.w,
    h: panel.min_height ?? MIN_PANEL_SIZE.h
  };
};

/**
 * Map a validated surface row onto the spec the panel registry stores.
 *
 * The manifest's snake_case is translated here, at the one boundary that
 * sees both vocabularies, and the contract's defaults are applied here too
 * rather than at each reader -- a default resolved in two places is a
 * default that will disagree with itself.
 *
 * `render` is a parameter because what a panel draws depends on its surface
 * kind, which is the caller's business: a webview draws an iframe, and a
 * json surface draws the payload the host fetched.
 */
export const toPanelSpec = (
  row: PluginSurfaceRow, render: () => React.ReactNode
): PanelSpec => {
  // Callers reach this only for rows whose form is `panel`, and the collector
  // requires a block on that form and validates it, so the block is both
  // present and well-formed here.
  const panel = row.surface.panel as PluginPanelConfig;
  return {
    id: pluginPanelId(row.plugin.name, row.key),
    title: panel.title,
    // Always a glyph, so nothing downstream has to invent one: a surface
    // that named no icon and one that named an icon this host lacks are the
    // same case to a reader -- something drawn beside a name, identifying
    // only that it is a plugin. `isIconGeneric` is how a surface that cannot
    // draw a name beside it knows to leave the glyph off.
    icon: resolvePanelIcon(panel.icon ?? FALLBACK_PANEL_ICON),
    isIconGeneric: panel.icon === undefined || !isKnownPanelIcon(panel.icon),
    size: { w: panel.width, h: panel.height },
    minSize: toMinSize(panel),
    anchor: panel.anchor,
    isResizable: panel.resizable ?? true,
    isClosable: panel.closable ?? true,
    preload: panel.preload ?? false,
    // Not from the manifest: every plugin-panel launcher lives on the desktop
    // header, which is the host's own layout and nothing an author declares.
    hasDesktopOnlyLauncher: true,
    render
  };
};
